import React, { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Home, Search } from "lucide-react";
import { CategoryMove } from "../../../server/Category";
import showAlert from "../../../server/ShowAlert";
import {
  isAtRootLevel,
  flattenCategoryTree,
  childBelongsToParent,
} from "./categorySearchUtils";

const ROOT_PARENT_SLUG = "_root";

const getCategoryName = (cat) => cat?.name ?? cat?.Name ?? "";

const MoveCategoryDialog = ({ toMove, handleClose }) => {
  const { allCategoriesState, stationId } = useSelector((state) => ({
    allCategoriesState: state.category.categories,
    stationId: state.user.stationId,
  }));

  const alreadyAtRoot = isAtRootLevel(toMove);
  const [showSearch, setShowSearch] = useState(alreadyAtRoot);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [isMoving, setIsMoving] = useState(false);
  const dispatch = useDispatch();

  const movingCategoryName = getCategoryName(toMove);

  const normalizedSearch = searchTerm.trim().toLowerCase();

  // Flatten the tree so nested subcategories are searchable, not just roots
  const flatCategories = useMemo(
    () => flattenCategoryTree(allCategoriesState),
    [allCategoriesState],
  );

  const searchResults = useMemo(() => {
    if (!normalizedSearch) return [];
    return flatCategories.filter((cat) => {
      if (cat.slug === toMove.slug) return false;
      return getCategoryName(cat).toLowerCase().includes(normalizedSearch);
    });
  }, [flatCategories, toMove.slug, normalizedSearch]);

  const handleMove = async () => {
    if (!selectedDestination || isMoving) return;

    try {
      setIsMoving(true);
      await dispatch(
        CategoryMove(toMove.slug, selectedDestination.slug, stationId),
      );
      handleClose(null);
      showAlert({
        msgTitle: "הקטגוריה הועברה בהצלחה",
        msgText: selectedDestination.isRoot
          ? `"${movingCategoryName}" הועברה לקטגוריה ראשית`
          : `"${movingCategoryName}" הועברה תחת "${selectedDestination.label}"`,
        msgType: "success",
      });
    } catch {
      showAlert({
        msgTitle: "העברת הקטגוריה נכשלה",
        msgType: "error",
      });
    } finally {
      setIsMoving(false);
    }
  };

  const handleSelectCategory = (cat) => {
    setSelectedDestination({
      slug: cat.slug,
      label: getCategoryName(cat),
      isRoot: false,
    });
  };

  const handleSelectRoot = () => {
    setSelectedDestination({
      slug: ROOT_PARENT_SLUG,
      label: "קטגוריה ראשית",
      isRoot: true,
    });
  };

  const goBackToChoose = () => {
    setShowSearch(false);
    setSearchTerm("");
    setSelectedDestination(null);
  };

  const getParentCategory = (cat) => {
    const parentCategory = flatCategories.find((c) =>
      childBelongsToParent(cat, c),
    );
    return parentCategory ? getCategoryName(parentCategory) : null;
  };

  const title = selectedDestination
    ? "אישור העברה"
    : "לאיזו קטגוריה ברצונך להעביר?";

  return (
    <div className="category-dialog-overlay">
      <div className="category-dialog-box category-move-dialog">
        <span className="close-icon" onClick={() => handleClose(null)}>
          ×
        </span>
        <h2>{title}</h2>

        {selectedDestination ? (
          <div className="category-move-review">
            <p className="category-move-review__label">יעד העברה</p>
            <p className="category-move-review__category">
              {selectedDestination.label}
            </p>

            <div className="category-move-review__actions">
              <button
                type="button"
                className="category-move-review__submit"
                onClick={handleMove}
                disabled={isMoving}
              >
                {isMoving ? "מעביר..." : "אישור העברה"}
              </button>
              <button
                type="button"
                className="category-move-review__change"
                onClick={() => setSelectedDestination(null)}
                disabled={isMoving}
              >
                שינוי יעד
              </button>
            </div>
          </div>
        ) : showSearch ? (
          <div className="category-move-search-step">
            <button
              type="button"
              className="category-move-back-btn"
              onClick={goBackToChoose}
            >
              חזרה
            </button>

            <p className="category-move-dialog__source">{movingCategoryName}</p>

            <input
              type="text"
              className="category-input category-move-search"
              placeholder="חפש קטגוריה..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />

            {normalizedSearch ? (
              <ul className="category-suggestions category-move-options">
                {searchResults.map((cat) => {
                  const parentName = getParentCategory(cat);
                  return (
                    <li
                      key={cat.slug}
                      role="button"
                      tabIndex={0}
                      onClick={() => handleSelectCategory(cat)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelectCategory(cat);
                        }
                      }}
                      className="category-item category-move-result"
                    >
                      <span className="category-move-result__name">
                        {getCategoryName(cat)}
                      </span>
                      {parentName && (
                        <span className="category-move-result__parent">
                          תחת: {parentName}
                        </span>
                      )}
                    </li>
                  );
                })}
                {searchResults.length === 0 && (
                  <li className="category-move-empty">לא נמצאו קטגוריות</li>
                )}
              </ul>
            ) : (
              <p className="category-move-search-hint">
                הקלד שם קטגוריה כדי לחפש במחלקות הקיימות
              </p>
            )}
          </div>
        ) : (
          <div className="category-move-choose">
            <p className="category-move-dialog__source">{movingCategoryName}</p>

            {!alreadyAtRoot && (
              <button
                type="button"
                className="category-move-option"
                onClick={handleSelectRoot}
              >
                <span className="category-move-option__icon" aria-hidden="true">
                  <Home size={22} strokeWidth={2} />
                </span>
                <span className="category-move-option__content">
                  <span className="category-move-option__title">
                    1. קטגוריה ראשית
                  </span>
                  <span className="category-move-option__desc">
                    העברה לרמת השורש — מחלקה ראשית בעץ
                  </span>
                </span>
              </button>
            )}

            <button
              type="button"
              className="category-move-option"
              onClick={() => setShowSearch(true)}
            >
              <span className="category-move-option__icon" aria-hidden="true">
                <Search size={22} strokeWidth={2} />
              </span>
              <span className="category-move-option__content">
                <span className="category-move-option__title">
                  {alreadyAtRoot ? "1." : "2."} חיפוש קטגוריה
                </span>
                <span className="category-move-option__desc">
                  חפש קטגוריה מתוך המחלקות הקיימות
                </span>
              </span>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MoveCategoryDialog;
